/**
 * usePendingPolls — returns count of poll posts the current user hasn't voted on yet.
 * Used to drive the nav badge on "Новости".
 */
import { useQuery } from '@tanstack/react-query';
import PostsAPI from '@/api/posts.api';
import PostPollAPI from '@/api/postPoll.api';
import { useAuthStore } from '@/stores/auth.store';

export function usePendingPolls() {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: ['pending-polls', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      const posts = await PostsAPI.getPosts();
      const pollPosts = posts.filter(p => !!p.poll);
      if (pollPosts.length === 0) return 0;

      // Fetch votes in parallel (with a concurrency cap)
      const results = await Promise.all(
        pollPosts.map(p => PostPollAPI.getResults(p.id).catch(() => []))
      );

      // Count posts where user hasn't voted
      let pending = 0;
      for (let i = 0; i < pollPosts.length; i++) {
        const hasVoted = results[i]?.some(v => v.userId === user.id);
        if (!hasVoted) pending++;
      }
      return pending;
    },
    enabled: !!user?.id,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
